import { useCallback, useEffect, useMemo, useState } from "react";
import { useFetchV2 } from "hooks/fetch/useFetchV2";

/**
 * Performs a get request on the url provided
 * @param url the url to get from
 * @param isBlocked if true a new request is not sent until it becomes false
 * @returns {{response: undefined, refetch: ((function(*=, *=): Promise<*>)), loading: boolean, error: boolean}}
 * loading - whether the request is currently in progress.
 *
 * error - whether the request is currently in an error state.
 *
 * response - the json response object, undefined if request not completed yet or null if errored or empty
 *
 * refetch - a callback function to refetch from the url.
 */
export function useFetchGetV2(url, isBlocked = false) {
  const [{ loading, error, response }, setFetchState] = useState({
    loading: true,
    error: false,
    response: undefined
  });
  const abortController = useMemo(() => {
    return new AbortController();
  }, []);

  // aborts any re-fetches currently in progress when the component unmounts
  useEffect(() => {
    return () => abortController.abort();
  }, [abortController]);

  const { callbackFunction } = useFetchV2();

  const getCallback = useCallback(
    (
      signal = abortController.signal,
      headers = { "Content-Type": "application/json" }
    ) => {
      setFetchState(prevState => {
        return { ...prevState, loading: true, error: false };
      });

      return callbackFunction(url, {
        method: "GET",
        headers,
        signal
      }).then(
        requestResponse => {
          setFetchState({
            loading: false,
            error: false,
            response: requestResponse
          });
          return Promise.resolve(requestResponse);
        },
        error => {
          setFetchState({ loading: false, error: true, response: undefined });
          return Promise.reject(error);
        }
      );
    },
    [url, abortController.signal, callbackFunction]
  );

  useEffect(() => {
    // create an abort controller which will just be used in this request
    const abortController = new AbortController();

    if (!isBlocked) {
      getCallback(abortController.signal).catch(() => {
        //We don't need to do anything when an error occurs as the state will be updated
        // However, we need to do this catch to prevent an error being thrown
      });
    } else {
      setFetchState({ loading: false, error: false, response: undefined });
    }

    // requests will be aborted if new requests come in before the old are finished
    // or when the component is unmounted
    return () => abortController.abort();
  }, [getCallback, isBlocked]);

  return {
    loading,
    error,
    response,
    refetch: getCallback
  };
}
